import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, Download } from 'lucide-react';

const TIPOS_FONASA = ['Activo', 'Menor', 'Pasivo', 'Conyuge'];

function DateInput({ value, onChange }) {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    if (value && !display) {
      setDisplay(value);
    }
  }, [value, display]);

  const handleChange = (e) => {
    let raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 8) raw = raw.slice(0, 8);

    let formatted = '';
    if (raw.length > 0) formatted += raw.slice(0, 2);
    if (raw.length > 2) formatted += '/' + raw.slice(2, 4);
    if (raw.length > 4) formatted += '/' + raw.slice(4, 8);

    setDisplay(formatted);
    if (raw.length === 8) {
      onChange(formatted);
    } else {
      onChange('');
    }
  };

  return (
    <input
      type="text"
      value={display}
      onChange={handleChange}
      placeholder="DD/MM/AAAA"
      maxLength={10}
      className="form-input"
      data-testid="fonasa-fecha-nac"
    />
  );
}

export default function FonasaRegistro() {
  const { user, canManage } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [mutualistas, setMutualistas] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [cedula, setCedula] = useState('');
  const [fechaNac, setFechaNac] = useState('');
  const [sexo, setSexo] = useState('');
  const [mutualista, setMutualista] = useState('');
  const [tipo, setTipo] = useState('');
  const [vendedorId, setVendedorId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [regs, consts] = await Promise.all([
        api.getFonasaRegistros(),
        api.getConstants()
      ]);
      setRegistros(regs);
      setMutualistas(consts.mutualistas || []);
      if (canManage) {
        const v = await api.getVendors();
        setVendors(v.filter(x => x.status === 'active'));
      }
    } catch (e) { /* handled */ }
    finally { setLoading(false); }
  }, [canManage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cedula || !fechaNac || !sexo || !mutualista || !tipo) {
      toast.error('Completar todos los campos');
      return;
    }
    setSaving(true);
    try {
      await api.createFonasaRegistro({
        cedula, fecha_nacimiento: fechaNac, sexo, mutualista_anterior: mutualista, tipo,
        vendedor_id: vendedorId || undefined
      });
      toast.success('Registro Fonasa guardado');
      setCedula(''); setFechaNac(''); setSexo(''); setMutualista(''); setTipo(''); setVendedorId('');
      fetchData();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este registro?')) return;
    try {
      await api.deleteFonasaRegistro(id);
      toast.success('Eliminado');
      setRegistros(prev => prev.filter(r => r.id !== id));
    } catch (e) { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6" data-testid="fonasa-registro-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Fonasa</h1>
        <p className="text-[13px] text-gray-500 mt-0.5">Registro de socios Fonasa</p>
      </div>

      <form onSubmit={handleSubmit} className="mac-card p-6 space-y-5" data-testid="fonasa-form">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Cedula</label>
            <input type="text" value={cedula} onChange={(e) => setCedula(e.target.value)}
              placeholder="Ej: 12345678" className="form-input" data-testid="fonasa-cedula" />
          </div>

          <div>
            <label className="form-label">Fecha de Nacimiento</label>
            <DateInput value={fechaNac} onChange={setFechaNac} />
          </div>

          <div>
            <label className="form-label">Sexo</label>
            <select value={sexo} onChange={(e) => setSexo(e.target.value)}
              className="form-input" data-testid="fonasa-sexo">
              <option value="">Seleccionar...</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>

          <div>
            <label className="form-label">Mutualista Anterior</label>
            <select value={mutualista} onChange={(e) => setMutualista(e.target.value)}
              className="form-input" data-testid="fonasa-mutualista">
              <option value="">Seleccionar...</option>
              {mutualistas.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Tipo</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}
              className="form-input" data-testid="fonasa-tipo">
              <option value="">Seleccionar...</option>
              {TIPOS_FONASA.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {canManage && (
            <div>
              <label className="form-label">Vendedor</label>
              <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)}
                className="form-input" data-testid="fonasa-vendedor">
                <option value="">Yo mismo</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
              </select>
            </div>
          )}
        </div>

        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="fonasa-submit">
          <Plus size={14} /> {saving ? 'Guardando...' : 'Registrar Fonasa'}
        </button>
      </form>

      {/* Table of registros */}
      <div className="mac-card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
            <FileText size={14} /> Registros ({registros.length})
          </h3>
          <button onClick={async () => {
              const XLSX = await import('xlsx');
              const rows = registros.map(r => ({
                Cedula: r.cedula,
                'Fecha Nac.': r.fecha_nacimiento,
                Sexo: r.sexo,
                'Mutualista Anterior': r.mutualista_anterior,
                Tipo: r.tipo,
                Vendedor: r.vendedor_nombre,
                'Fecha Registro': new Date(r.fecha).toLocaleDateString('es-UY')
              }));
              const ws = XLSX.utils.json_to_sheet(rows);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Fonasa');
              XLSX.writeFile(wb, 'Registros_Fonasa.xlsx');
            }} className="btn-ghost flex items-center gap-2 text-[12px]" data-testid="fonasa-export-excel">
              <Download size={14} /> Excel
            </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="fonasa-registros-table">
            <thead>
              <tr>
                <th className="table-header">Cedula</th>
                <th className="table-header">Fecha Nac.</th>
                <th className="table-header">Sexo</th>
                <th className="table-header">Mutualista Ant.</th>
                <th className="table-header">Tipo</th>
                <th className="table-header">Vendedor</th>
                <th className="table-header">Fecha Reg.</th>
                {user?.role === 'auditor' && <th className="table-header w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="table-cell text-center text-gray-500">Cargando...</td></tr>
              ) : registros.length === 0 ? (
                <tr><td colSpan={8} className="table-cell text-center text-gray-500">No hay registros</td></tr>
              ) : registros.map(r => (
                <tr key={r.id} className="table-row">
                  <td className="table-cell font-medium">{r.cedula}</td>
                  <td className="table-cell">{r.fecha_nacimiento}</td>
                  <td className="table-cell">{r.sexo}</td>
                  <td className="table-cell">{r.mutualista_anterior}</td>
                  <td className="table-cell">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                      r.tipo === 'Activo' ? 'bg-green-100 text-green-700' :
                      r.tipo === 'Menor' ? 'bg-blue-100 text-blue-700' :
                      r.tipo === 'Pasivo' ? 'bg-amber-100 text-amber-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>{r.tipo}</span>
                  </td>
                  <td className="table-cell">{r.vendedor_nombre}</td>
                  <td className="table-cell text-[11px] text-gray-500">
                    {new Date(r.fecha).toLocaleDateString('es-UY')}
                  </td>
                  {user?.role === 'auditor' && (
                    <td className="table-cell">
                      <button onClick={() => handleDelete(r.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-fonasa-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
